<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { onMounted } from 'vue';
import { useMessageCenterStore } from '@/store';
import { useAuthStore } from '@/store';
import MessageCenterBase from './components/MessageCenterBase';
import Chat from './components/Chat';
import ChatList from './components/ChatList';
import NewChat from './components/NewChat';
import { useTenant, can } from '@/composables';
import { MESSAGE_CENTER_EVENTS, TENANT_SETTINGS } from '@/enum';
import { echoService } from '@/utils';
import { ChatInterface, SyncDataFileMessage, SyncDataMessage } from '@/types/MessageCenter';
import DisplayChatName from './components/DisplayChatName';
import { PERMISSIONS } from '@/enum';
const authStore = useAuthStore();
const messageCenter = useMessageCenterStore();
const { screen } = storeToRefs(messageCenter);

const $tenant = useTenant();

const isMessageCenterEnabled = () => {
  return $tenant.tenantSettingEnabled(TENANT_SETTINGS.MESSAGE_CENTER) && !authStore.isImpersonating;
};

const enableMessageCenter = isMessageCenterEnabled();

onMounted(() => init());

const init = async () => {
  if (!enableMessageCenter) return;
  messageCenter.$reset();
  messageCenter.getChats();
  messageCenter.getMessageSettings();
  screen.value = 'list-chats';
  setUpEcho();
};

const setUpEcho = async () => {
  const token = await authStore.token;
  const tenant = token?.tenant.id;
  if (!token) return;

  echoService.createEcho(token.access_token, tenant);
  echoService
    .getEcho()
    ?.private(`message_center.${authStore.user?.id}`)
    .listen(MESSAGE_CENTER_EVENTS.NEW_MESSAGE_SENT, (data: SyncDataMessage) => {
      messageCenter.syncDataMessage(data);
    })
    .listen(MESSAGE_CENTER_EVENTS.CHAT_CREATED, (chat: ChatInterface) => {
      messageCenter.chatCreated(chat);
    })
    .listen(MESSAGE_CENTER_EVENTS.CHAT_DELETED, (chat: ChatInterface) => {
      messageCenter.chatDeleted(chat);
    })
    .listen(MESSAGE_CENTER_EVENTS.MESSAGE_DELETED, (data: SyncDataMessage) => {
      messageCenter.messageDeleted(data);
    })
    .listen(MESSAGE_CENTER_EVENTS.MESSAGE_FILE_DELETED, (data: SyncDataFileMessage) => {
      messageCenter.messageFileDeleted(data);
    });
};

const openChatList = () => {
  screen.value = 'list-chats';
  messageCenter.currentChatId = undefined;
};

const goListChats = () => {
  screen.value = 'list-chats';
  messageCenter.currentChatId = undefined;
};
</script>

<template>
  <MessageCenterBase v-if="enableMessageCenter">
    <template #header>
      <div v-if="messageCenter.screen === 'list-chats'" class="align-items-center flex justify-content-center">
        <span class="font-semibold text-xl text-primary-700">Messages</span>
      </div>
      <div v-if="messageCenter.screen === 'new-chat'" class="align-items-center flex justify-content-center">
        <Icon
          name="arrow_back_ios"
          class="icon-color font-bold cursor-pointer text-base text-color-secondary"
          @click="goListChats"
        />
        <h3 class="font-bold ml-2 text-color-secondary">New Message</h3>
      </div>
      <div v-if="messageCenter.screen === 'chat'" class="align-items-center flex justify-content-center">
        <Icon
          name="arrow_back_ios"
          class="icon-color font-bold cursor-pointer text-base text-color-secondary"
          @click="openChatList"
        />
      </div>
      <h3 class="font-bold ml-2">
        <DisplayChatName
          v-if="messageCenter.getCurrentChat"
          :name="messageCenter.getCurrentChat.name"
          class="max-w-14rem"
        />
      </h3>
    </template>
    <ChatList v-if="messageCenter.screen === 'list-chats' && can([PERMISSIONS.CHAT_INDEX])" />
    <NewChat v-if="messageCenter.screen === 'new-chat' && can([PERMISSIONS.CHAT_STORE])" />
    <Chat v-if="messageCenter.screen === 'chat'" />
  </MessageCenterBase>
</template>
